import Vue from 'vue';
import { ActionTree, MutationTree, GetterTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import moment from 'moment';
import { QueryBuilder } from '@/core/uql';
import cloneDeep from 'lodash/cloneDeep';
import { fetchEnumsFromSchema } from '@/core/tools/forms/fetch_enums_from_schema';

interface DealDetailsState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;

    document: any;

    inWorkStatus: any;
    formationStatus: any;
    rejectStatus: any;
    products: any[];
    remains: any;
    meta: any;
}

const state: DealDetailsState = {
    loading: false,
    loaded: false,
    fatalError: false,

    document: null,

    inWorkStatus: null,
    formationStatus: null,
    rejectStatus: null,
    products: [],
    remains: null,
    meta: {},
};

const initState: any = cloneDeep(state);

export const DEAL_DATA = 'GET_DEAL_DATA';
export const LOAD_DEAL_PRODUCTS = 'LOAD_DEAL_PRODUCTS';
export const GET_CLIENT_PREV_DEALS_COUNT = 'GET_CLIENT_PREV_DEALS_COUNT';
export const GET_CORRECTIONS_COUNT = 'GET_CORRECTIONS_COUNT';
export const GET_WORK_STATUS = 'GET_WORK_STATUS';
export const GET_FORMATION_STATUS = 'GET_FORMATION_STATUS';
export const GET_REJECT_STATUS = 'GET_REJECT_STATUS';

interface PipelineStatuses {
  inWorkStatus: any;
  formationStatus: any;
  rejectStatus: any;
}

let pipelineStatusesCache: PipelineStatuses | null = null;

function loadPipelineStatuses(vm: any): Promise<PipelineStatuses> {
  if (pipelineStatusesCache) {
    return Promise.resolve(pipelineStatusesCache);
  }

  return vm
    .$db([
      (vm.$q(GET_WORK_STATUS) as QueryBuilder)
        .select()
        .from('statuses')
        .fileds({
          id: 'statuses.id',
        })
        .leftJoin('pipelines', (_: any) => {
          return _.eq('statuses.pipeline_id', 'pipelines.id');
        })
        .where((_) => {
          return _.and([
            _.eq('pipelines.code', _.const('active')),
            _.eq('statuses.code', _.const('in_work')),
          ]);
        })
        .setNoMeta(),

      (vm.$q(GET_FORMATION_STATUS) as QueryBuilder)
        .select()
        .from('statuses')
        .fileds({
          id: 'statuses.id',
        })
        .leftJoin('pipelines', (_: any) => {
          return _.eq('statuses.pipeline_id', 'pipelines.id');
        })
        .where((_) => {
          return _.and([
            _.eq('pipelines.code', _.const('active')),
            _.eq('statuses.code', _.const('formation')),
          ]);
        })
        .setNoMeta(),

      (vm.$q(GET_REJECT_STATUS) as QueryBuilder)
        .select()
        .from('statuses')
        .fileds({
          id: 'statuses.id',
        })
        .leftJoin('pipelines', (_: any) => {
          return _.eq('statuses.pipeline_id', 'pipelines.id');
        })
        .where((_) => {
          return _.eq('statuses.final', _.const('fail'));
        })
        .setNoMeta(),
    ])
    .then((result: ServerResult) => {
      pipelineStatusesCache = {
        inWorkStatus: result
          .getOperation(GET_WORK_STATUS)
          .getContent().documents[0],
        formationStatus: result
          .getOperation(GET_FORMATION_STATUS)
          .getContent().documents[0],
        rejectStatus: result
          .getOperation(GET_REJECT_STATUS)
          .getContent().documents[0],
      };

      return pipelineStatusesCache;
    });
}

function applyDealProductCounts(deal: any, products: any[]) {
  deal.count_product_all = products.length;
  deal.count_product_paid = products.filter(
    (item) =>
      !item.product_container_is_sample && !item.product_pick_up_product,
  ).length;
  deal.unconfirmed_items = products.filter(
    (item) =>
      item.product_container_is_sample &&
      item.product_container_approve_required_from_id &&
      (item.product_container_is_approved === false ||
        item.product_container_is_approved == null),
  ).length;
}

export function clientPrevDealsCountQuery(dealId: string) {
  return {
    no_meta: true,
    columns: {
      count: { $count: 'ds.id' },
    },
    from: 's:deals',
    from_as: 'ds',
    joins: [
      {
        join: 's:view_deals_totals',
        as: 'vdt',
        on: { $eq: ['ds.id', 'vdt.id'] },
      },
    ],
    where: {
      $and: [
        { $gt: ['vdt.total_weight', { $: '0' }] },
        {
          $in: [
            'ds.client_id',
            {
              columns: { client_id: 'd.client_id' },
              from: 's:deals',
              from_as: 'd',
              where: { $eq: ['d.id', { $: dealId }] },
            },
          ],
        },
      ],
    },
  };
}

export function dealCorrectionsCountQuery(dealId: string) {
  return {
    no_meta: true,
    columns: {
      count: { $count: 'dpcc.id' },
    },
    from: 's:deal_product_containers_corrections',
    from_as: 'dpcc',
    where: { $eq: ['dpcc.deal_id', { $: dealId }] },
  };
}

export function productsQuery(docId: string) {
  return {
    no_meta: true,
    columns: {
      id: 'basket_item.id',
      container_weight: 'basket_item.container_weight',
      container_quantity: 'basket_item.container_quantity',
      container_price: 'basket_item.container_price',
      notes: 'basket_item.notes',
      approve_comment: 'basket_item.approve_comment',
      created_at: 'basket_item.created_at',
      updated_at: 'basket_item.updated_at',
      product_container_id: 'variant.id',
      product_container_name: 'variant.name',
      product_container_weight: 'variant.weight',
      // product_container_price: 'personal_settings.container_price',
      product_container_price_per_kg: 'variant.price_per_kg',
      product_container_is_sample: 'basket_item.is_sample',
      product_container_approve_required_from_id: 'basket_item.approve_required_from_id',
      product_container_is_approved: 'basket_item.is_approved',
      product_id: 'product.id',
      product_name: 'product.name',
      product_pick_up_product: 'product.pick_up_product',
      sample_date: 'basket_item.sample_date'
    },

    from: 's:deal_product_containers',
    from_as: 'basket_item',
    joins: [
      {
        join: 's:product_containers',
        type: 'left',
        as:   'variant',
        on: {
          $eq: ['basket_item.product_container_id', 'variant.id']
        }
      },
      {
        join: 's:products',
        type: 'left',
        as:   'product',
        on: {
          $eq: ['variant.product_id', 'product.id']
        }
      },
      // {
      //   join: 's:client_product_containers',
      //   type: 'left',
      //   as:   'personal_settings',
      //   on: {
      //     $and: [
      //       { $eq: ['personal_settings.product_container_id', 'variant.id'] },
      //       { $eq: ['personal_settings.client_id', {$: clientId } ] }
      //     ]
      //   }
      // }
    ],
    page_size: 100,
    order: [
      {$desc: ['basket_item.id']}
    ],
    where: {
      $eq: [`basket_item.deal_id`, {$: docId}]
    }
  }
}

export function productDetails(docId: string) {
  const columns: any = {
    id: 'deal.id',
    name: 'deal.name',
    created_at: 'deal.created_at',
    updated_at: 'deal.updated_at',
    type_id: 'deal.type_id',
    deal_comment: 'deal.deal_comment',

    confirmed_at: 'deal.confirmed_at',
    approved_at: 'deal.approved_at',
    approve_result: 'deal.approve_result',
    approve_by_id: 'deal.approve_by_id',
    approve_state: 'deal.approve_state',

    operator_approved_id: 'deal.operator_approved_id',
    logist_approved_id: 'deal.logist_approved_id',
    accountant_approved_id: 'deal.accountant_approved_id',
    operator_name: 'operator_approved.name',
    logist_name: 'logist_approved.name',
    accountant_name: 'accountant_approved.name',

    client_id: 'client.id',
    client_name: 'client.name',
    delivery_warehouse_id: 'deal.delivery_warehouse_id',
    destination_warehouse_id: 'deal.destination_warehouse_id',
    client_type_id: 'client.type_id',

    pipeline_id: 'pipeline.id',
    pipeline_code: 'pipeline.code',
    pipeline_name: 'pipeline.name',

    status_id: 'status.id',
    status_color: 'status.color',
    status_code: 'status.code',
    status_name: 'status.name',

    manager_id: 'employee.id',
    manager_name: 'employee.name',

    counterparty_id: 'counterparty.id',
    counterparty_name: 'counterparty.name',
    counterparty_inn: 'counterparty.inn',
    counterparty_kpp: 'counterparty.kpp',

    point_city: 'city.name',
    point_address: 'point.address',
    point_destination_aaddress: 'point_destination.address',
    warehouse_name: 'warehouse.name',
    destination_warehouse: 'destination_warehouse.name',

    expected_delivery_date: 'deal.expected_delivery_date',
    expected_payment_date: {$add: ['deal.expected_delivery_date', 'pdp.period']},

    mixcart_mixcart_id: 'mixcart_deals.mixcart_id',
    mixcart_is_reject: 'mixcart_deals_rej.is_reject',
    mixcart_manual_processed: 'mixcart_deals_rej.manual_processed',
    mixcart_id: 'mixcart_deals_rej.id',
    created_by_name: {
      $case: [
        [
          { $not: [ { $null: ['created_employee.id'] } ] },
          'created_employee.name'
        ],
        [
          { $not: [ { $null: ['created_people.id'] } ] },
          'created_people.name',
        ]
      ]
    },
    source: 'deal.source',
    is_corrected: 'deal.is_corrected',
  };

  return {
      columns,
      no_meta: true,
      from: 's:deals',
      from_as: 'deal',
      joins: [
        {
          join: 's:clients',
          as:   'client',
          on: {
            $eq: ['client.id', 'deal.client_id']
          }
        },
        {
          join: 's:payment_deferment_periods',
          type: 'left',
          as:   'pdp',
          on:   {$eq: ['pdp.id', 'client.payment_deferment_period_id']}
        },

        {
          join: 's:employees',
          type: 'left',
          as:   'employee',
          on: {
            $eq: ['employee.id', 'deal.manager_id']
          }
        },

        {
          join: 's:counterparties',
          type: 'left',
          as:   'counterparty',
          on: {
            $eq: ['deal.counterparty_id', 'counterparty.id']
          }
        },

        {
          join: 's:delivery_points',
          as:   'point',
          on: {
            $eq: ['deal.delivery_point_id', 'point.id']
          }
        },

        {
          join: 's:delivery_points',
          type: 'left',
          as:   'point_destination',
          on: {
            $eq: ['deal.destination_delivery_point_id', 'point_destination.id']
          }
        },
        {
          join: 's:cities',
          as:   'city',
          on: {
            $eq: ['point.city_id', 'city.id']
          }
        },

        {
          join: 's:warehouses',
          type: 'left',
          as:   'warehouse',
          on: {
            $eq: ['deal.delivery_warehouse_id', 'warehouse.id']
          }
        },

        {
          join: 's:warehouses',
          type: 'left',
          as:   'destination_warehouse',
          on: {
            $eq: ['deal.destination_warehouse_id', 'destination_warehouse.id']
          }
        },

        {
          join: 's:statuses',
          as:   'status',
          on: {
            $eq: ['status.id', 'deal.status_id']
          }
        },
        {
          join: 's:pipelines',
          type: 'left',
          as:   'pipeline',
          on: {
            $eq: ['pipeline.id', 'status.pipeline_id']
          }
        },
        {
          join: 's:employees',
          type: 'left',
          as:   'created_employee',
          on: {
            $eq: ['deal.created_by_id', 'created_employee.id']
          }
        },
        {
          join: 's:people',
          type: 'left',
          as:   'created_people',
          on: {
            $eq: ['deal.created_by_id', 'created_people.id']
          }
        },
        {
            join: 's:mixcart_deals',
            type: 'left',
            as:   'mixcart_deals_rej',
            on: {
                $and: [
                  {$eq: ['mixcart_deals_rej.deal_id', 'deal.id']},
                  {$eq: ['mixcart_deals_rej.is_reject', {$: true}]}
                ]
            }
        },
        {
            join: 's:mixcart_deals',
            type: 'left',
            as:   'mixcart_deals',
            on: {
                $and: [
                  {$eq: ['mixcart_deals.deal_id', 'deal.id']},
                ]
            }
        },
        {
          join: 's:employees',
          type: 'left',
          as:   'operator_approved',
          on: {
            $eq: ['deal.operator_approved_id', 'operator_approved.id']
          }
        },
        {
          join: 's:employees',
          type: 'left',
          as:   'logist_approved',
          on: {
            $eq: ['deal.logist_approved_id', 'logist_approved.id']
          }
        },
        {
          join: 's:employees',
          type: 'left',
          as:   'accountant_approved',
          on: {
            $eq: ['deal.accountant_approved_id', 'accountant_approved.id']
          }
        },
      ],
      where: {
        $eq: [`deal.id`, {$: docId}]
      }
  }
}

export const actions: ActionTree<DealDetailsState, RootState> = {
    reset({ commit }) {
        commit('RESET_STATE')
    },

    loadData({ commit, getters, dispatch, state }, params) {
        const vm = (this as any)._vm;

        if (params.id == 'new') {
            state.document = {};
            state.loaded = true
        } else {
            return new Promise((resolve, reject) => {
                if (!params.silence) {
                    commit('START_LOADING');
                }

                vm.$db([
                    vm.$q(DEAL_DATA)
                      .raw('documents.list')
                      .action(productDetails(params.id)),

                  (vm.$q(LOAD_DEAL_PRODUCTS) as QueryBuilder)
                      .raw('documents.list')
                      .action(productsQuery(params.id)),

                  vm
                    .$q(GET_CLIENT_PREV_DEALS_COUNT)
                    .raw('documents.list')
                    .action(clientPrevDealsCountQuery(params.id)),

                  vm
                    .$q(GET_CORRECTIONS_COUNT)
                    .raw('documents.list')
                    .action(dealCorrectionsCountQuery(params.id)),
                ]).then(((result: ServerResult) => {
                    const dealData = result.getOperation(DEAL_DATA).getContent();
                    const productsData = result
                      .getOperation(LOAD_DEAL_PRODUCTS)
                      .getContent();
                    const prevDealsCount = result
                      .getOperation(GET_CLIENT_PREV_DEALS_COUNT)
                      .getContent().documents[0];
                    const correctionsCount = result
                      .getOperation(GET_CORRECTIONS_COUNT)
                      .getContent().documents[0];

                    const document = dealData.documents[0];
                    const products = productsData.documents;

                    document.count_prev_deals = prevDealsCount
                      ? prevDealsCount.count
                      : 0;
                    document.corrections_count = correctionsCount
                      ? correctionsCount.count
                      : 0;
                    applyDealProductCounts(document, products);

                    commit('SET_DEAL_DATA', {
                      ...dealData,
                      documents: [document],
                    });
                    commit('SET_DEAL_PRODRUCTS', productsData);

                    loadPipelineStatuses(vm).then((statuses) => {
                      commit('SET_STATUSES_INFO', statuses);
                    });

                    if (!params.silence) {
                      commit('LOADED_SUCCESS');
                    }

                    dispatch('loadRemains');
                    resolve();
                }));
            })
        }
    },

    loadRemains({commit, state}) {
        const vm = (this as any)._vm;
        const date = (state.document.expected_delivery_date ? moment(state.document.expected_delivery_date) : moment()).format('YYYY-MM-DD')
        return new Promise((resolve) => {
          if (state.document.delivery_warehouse_id) {
            vm.$db([
                vm.$q("SELECT_WAREHOUSE")
                    .raw('warehouse')
                    .action({
                        warehouse_id: state.document.delivery_warehouse_id,
                        date: date,
                        deal_id: state.document.id
                    }),
            ]).then(((result: ServerResult) => {
                commit('SET_REMAINS_DATA', result.getOperation("SELECT_WAREHOUSE").getContent());
                resolve();
            }));
          } else {
            resolve();
          }
        });
    },

    toWork({ state, dispatch }) {
      const vm = (this as any)._vm;

      vm.$db([
          (vm.$q('DEAL_TO_WORK') as QueryBuilder)
            .update(state.document.id, { status_id: state.formationStatus.id })
      ])
      .then(() => {
          dispatch('loadData', { id: state.document.id})
      })
  },
    setCorrected({ state, dispatch }) {
      const vm = (this as any)._vm;

      vm.$db([
          (vm.$q('DEAL_TO_WORK') as QueryBuilder)
            .update(state.document.id, { is_corrected: true} )
      ])
      .then(() => {
          dispatch('loadData', { id: state.document.id})
      })
  },

  toFormation({ state, dispatch }) {
      const vm = (this as any)._vm;

      vm.$db([
          (vm.$q('DEAL_TO_FORMATION') as QueryBuilder)
            .update(state.document.id, { status_id: state.formationStatus.id })
      ])
      .then(() => {
          dispatch('loadData', { id: state.document.id})
      })
  },

  toReject({ state, dispatch }, is_reject) {
      const vm = (this as any)._vm;
      const queries = [
        (vm.$q('MIXCART_PROCESSED') as QueryBuilder)
            .update(state.document.mixcart_id, { manual_processed: true })
      ]
      if (is_reject) {
        queries.push(
          (vm.$q('DEAL_TO_REJECT') as QueryBuilder)
            .update(state.document.id, { status_id: state.rejectStatus.id })
          )
      }

      vm.$db(queries)
      .then(() => {
          dispatch('loadData', { id: state.document.id})
      })
  },

  confirmBasket({ state, dispatch }) {
      const vm = (this as any)._vm;

      vm.$db([
          (vm.$q('PL_CONFIRM_BASKET') as QueryBuilder)
            .update(state.document.id, { approve_result: true })
      ])
      .then(() => {
          dispatch('loadData', { id: state.document.id})
      })

  },
  confirmDeal({ state, rootState, dispatch }, field_id) {
      const vm = (this as any)._vm;
      const doc: any = {}

      doc[field_id] = (rootState as any).ubic.actor.document.id

      vm.$db([
          (vm.$q('PL_CONFIRM_BASKET') as QueryBuilder)
            .update(state.document.id, doc)
      ])
      .then(() => {
          dispatch('loadData', { id: state.document.id})
      })

  }
};

export const mutations: MutationTree<DealDetailsState> = {
  RESET_STATE: (state) => {
      for(const key in initState) {
          (state as any)[key] = initState[key]
      }
  },
  START_LOADING: (state) => {
      state.loaded = false;
      state.loading = true;
      state.fatalError = false;
  },
  LOADED_SUCCESS: (state) => {
      state.loaded = true;
      state.loading = false;
      state.fatalError = false;
  },
  LOADED_ERROR: (state) => {
      state.loaded = true;
      state.loading = false;
      state.fatalError = true;
  },

  SET_REMAINS_DATA(state, data) {
      state.remains = data.data.map((item: any) => {
        item.availability.splice(item.availability.length - 2, 2)
        item.availability = [0, ...item.availability]
        item.expected.splice(item.expected.length - 1, 1)
        item.movement.splice(item.movement.length - 1, 1)
        return item
      })
  },

  SET_DEAL_DATA(state, data) {
      const schema = data.schema;
      const document = data.documents[0];
      state.meta = fetchEnumsFromSchema(schema);

      state.document = document
  },

  SET_DEAL_PRODRUCTS(state, data) {
      const products = data.documents.map((item: any) => {
        if (item.sample_date) {
          item.sample_date = moment(item.sample_date).toDate();
        }
        return item
      });
      state.products = products
  },

  SET_STATUSES_INFO(state, {inWorkStatus, formationStatus, rejectStatus}) {
      state.inWorkStatus = inWorkStatus;
      state.formationStatus = formationStatus;
      state.rejectStatus = rejectStatus;
  },
};

export const getters: GetterTree<DealDetailsState, RootState> = {
  canActionToWork: (state: DealDetailsState) => {
      return state.document.pipeline_code === 'active' && state.document.status_code === 'new';
  },
  canActionConfirmAction: (state: DealDetailsState) => {
      return (!!state.document.approve_by_id && state.document.approve_result === null);
  },
  disableActionConfirmActionCall: (state, getters, rootState) => {
      return !(state.document.approve_result === null && state.document.approve_by_id == (rootState as any).ubic.actor.document.id)
  },
  canActionToFormation: (state, getters, rootState) => {
      return !getters.canActionConfirmAction && (state.document.pipeline_code === 'active' && state.document.status_code === 'in_work');
  },
  hasActions: (state, getters, rootState) => {
      return getters.canActionToWork || getters.canActionConfirmAction || getters.canActionToFormation
  }
};

export const STORE_KEY = '$_deal_details';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
